import { StateManager, segmentId } from '../../managers/StateManager';
import { PixiAppRefs } from './types';

/** Section ids (bundle ids) a wire routes through; empty when the wire is unknown. */
export function wireSegmentIds(stateManager: StateManager, wireId?: string): Set<string> {
  const ids = new Set<string>();
  if (!wireId) {
    return ids;
  }
  const wire = stateManager.getState().wires.find(w => w.id === wireId);
  if (!wire) {
    return ids;
  }
  // A same-connector jumper has no sections; its bundle is keyed by the wire id.
  if (wire.startConnectorId === wire.endConnectorId && wire.path.length === 0) {
    ids.add(wire.id);
    return ids;
  }
  const route = stateManager.getWireRoute(wire);
  for (let i = 0; i < route.length - 1; i++) {
    ids.add(segmentId(route[i], route[i + 1]));
  }
  return ids;
}

/**
 * Builds a stateful highlighter that toggles the highlight on the wire-bundle
 * sections of the currently highlighted wire, lighting only that wire's own
 * breakout within each shared section. It diffs against the previous state so
 * only the bundles that changed are redrawn, keeping frequent interaction-state
 * updates (such as wire-creation moves) cheap.
 */
export function createWireHighlighter(refs: PixiAppRefs): (highlighted: Set<string>, wireId?: string) => void {
  let current = new Set<string>();
  let currentWireId: string | undefined;

  return (next: Set<string>, wireId?: string): void => {
    const bundles = refs.wireBundleEntities.current;
    // A change of wire can keep the same sections but move which stub lights up,
    // so re-apply every section when the highlighted wire itself changes.
    if (wireId !== currentWireId) {
      current.forEach(id => bundles.get(id)?.setHighlighted(false));
      next.forEach(id => bundles.get(id)?.setHighlighted(true, wireId));
    } else {
      current.forEach(id => {
        if (!next.has(id)) {
          bundles.get(id)?.setHighlighted(false);
        }
      });
      next.forEach(id => {
        if (!current.has(id)) {
          bundles.get(id)?.setHighlighted(true, wireId);
        }
      });
    }
    current = next;
    currentWireId = wireId;
  };
}
